/**
 * Genesis Comic V2 — optional direct wallet mint (Sepolia test only).
 *
 * Env (Create React App):
 *   REACT_APP_NFT_COMIC_V2_MINT_ENABLED=true|false
 *   REACT_APP_NFT_COMIC_CONTRACT_ADDRESS_V2=0x...
 *   REACT_APP_NFT_COMIC_CHAIN_ID_V2=11155111
 *   REACT_APP_NFT_COMIC_IMAGE_BASE_URI_V2=https://...mypinata.cloud/ipfs/<folder-cid>/  (optional; My Library cover fallback)
 */
import { ethers } from "ethers";
import { isProductionBuild } from "../config/publicEnv";

function envBool(name) {
  const v = String(process.env[name] || "").trim().toLowerCase();
  return v === "true" || v === "1" || v === "yes";
}

/** Explicit opt-in; default off. */
export function isNftComicV2MintFlagEnabled() {
  return envBool("REACT_APP_NFT_COMIC_V2_MINT_ENABLED");
}

export function getNftComicV2ContractAddressRaw() {
  return String(process.env.REACT_APP_NFT_COMIC_CONTRACT_ADDRESS_V2 || "").trim();
}

/** Optional; when set with contract+token match, client can synthesize `${base}${tokenId}.png`. */
export function getNftComicV2ImageBaseUriRaw() {
  return String(process.env.REACT_APP_NFT_COMIC_IMAGE_BASE_URI_V2 || "").trim();
}

export function getNftComicV2ChainId() {
  const raw = String(process.env.REACT_APP_NFT_COMIC_CHAIN_ID_V2 ?? "").trim();
  if (!raw) {
    if (isProductionBuild()) {
      throw new Error(
        "Missing REACT_APP_NFT_COMIC_CHAIN_ID_V2 for production builds. See .env.example."
      );
    }
    return 11155111;
  }
  const n = parseInt(raw, 10);
  if (!Number.isFinite(n)) {
    throw new Error("Invalid REACT_APP_NFT_COMIC_CHAIN_ID_V2: must be a numeric chain id.");
  }
  return n;
}

/**
 * UI + flows: flag on, valid checksummable contract on configured V2 chain.
 */
export function isNftComicV2MintUiVisible() {
  if (!isNftComicV2MintFlagEnabled()) return false;
  const raw = getNftComicV2ContractAddressRaw();
  if (!raw) return false;
  try {
    ethers.utils.getAddress(raw);
    return Number.isFinite(getNftComicV2ChainId());
  } catch {
    return false;
  }
}

export function getNftComicV2ContractAddressChecksummed() {
  const raw = getNftComicV2ContractAddressRaw();
  if (!raw) return null;
  try {
    return ethers.utils.getAddress(raw);
  } catch {
    return null;
  }
}

/** True when candidate address matches configured V2 comic contract (case-insensitive). */
export function isSameNftComicV2Contract(candidateRaw) {
  const envCa = getNftComicV2ContractAddressChecksummed();
  if (!envCa || candidateRaw == null) return false;
  try {
    return ethers.utils.getAddress(String(candidateRaw).trim()).toLowerCase() === envCa.toLowerCase();
  } catch {
    return false;
  }
}
